/**
 * The edit ids a profile row opens with `?edit=<id>`, kept from A2
 * (`profile/editors.tsx:36-48`, the `EditId` union and its switch), and the
 * endpoint each one writes to.
 *
 * ── `pain:<index>`, NOT `pain:<id>` ─────────────────────────────────────────
 * A2 gave every pain an id. The contract's injury report has none: the list
 * is the resource and `PUT /me/injuries` replaces it whole. A stored report is
 * therefore addressed by its position in the list the server last answered,
 * which is stable until the next write — and the next write is the one this
 * editor makes.
 *
 * ── `email` IS NOT AN EDITOR ────────────────────────────────────────────────
 * There is no change-email endpoint. The row is read-only: T51 shows the
 * address and opens nothing, and `EditorSheet` renders nothing for it.
 */

export const EDITOR_IDS = [
  "name",
  "birth",
  "sex",
  "height",
  "side",
  "motivation",
  "weight",
  "bodyFat",
  "goal",
  "secondary",
  "experience",
  "stoppedAgo",
  "trainedFor",
  "same",
  "days",
  "sessionMinutes",
  "place",
  "equipment",
  "occupation",
  "sleep",
  "protein",
  "supplement",
  "modalities",
  "parq",
  "reminder",
  "pain-new",
  "export",
  "export-csv",
  "import",
  "gym-new",
  "logout",
  "delete",
] as const;

export type StaticEditorId = (typeof EDITOR_IDS)[number];

export type PainEditId = `pain:${number}`;

/** One saved gym (`D202`): `gym:<id>`, the device-minted UUIDv7. */
export type GymEditId = `gym:${string}`;

export type EditorId = StaticEditorId | PainEditId | GymEditId;

const STATIC = new Set<string>(EDITOR_IDS);
const PAIN_ID = /^pain:(0|[1-9]\d*)$/;
/** The id the server echoes is the device's UUIDv7; the URL's `edit` is 40
 *  characters, which `gym:` plus 36 fills exactly. */
const GYM_ID = /^gym:([0-9A-Za-z-]{1,36})$/;

export function isEditorId(value: string): value is EditorId {
  return STATIC.has(value) || PAIN_ID.test(value) || GYM_ID.test(value);
}

export function painEditId(index: number): PainEditId {
  return `pain:${index}`;
}

/** The list position a `pain:<n>` id names, or `null` for any other id. */
export function painIndexOf(id: string): number | null {
  const match = PAIN_ID.exec(id);
  return match?.[1] === undefined ? null : Number(match[1]);
}

export function gymEditId(gymId: string): GymEditId {
  return `gym:${gymId}`;
}

/** The gym a `gym:<id>` names, or `null` for any other id. */
export function gymIdOf(id: string): string | null {
  return GYM_ID.exec(id)?.[1] ?? null;
}

/** Which write an editor ends in. */
export type EditorEndpoint =
  | "account-delete"
  | "display-name"
  | "equipment"
  | "export"
  | "gyms"
  | "imports"
  | "injuries"
  | "measurements"
  | "notification-preferences"
  | "parq"
  | "profile"
  | "sign-out"
  | "training-config";

const ENDPOINTS: Readonly<Record<StaticEditorId, EditorEndpoint>> = {
  name: "display-name",
  birth: "profile",
  sex: "profile",
  height: "profile",
  motivation: "profile",
  occupation: "profile",
  sleep: "profile",
  protein: "profile",
  supplement: "profile",
  modalities: "profile",
  side: "training-config",
  goal: "training-config",
  secondary: "training-config",
  experience: "training-config",
  stoppedAgo: "training-config",
  trainedFor: "training-config",
  days: "training-config",
  same: "training-config",
  sessionMinutes: "training-config",
  weight: "measurements",
  bodyFat: "measurements",
  place: "equipment",
  equipment: "equipment",
  "pain-new": "injuries",
  parq: "parq",
  reminder: "notification-preferences",
  export: "export",
  "export-csv": "export",
  import: "imports",
  "gym-new": "gyms",
  logout: "sign-out",
  delete: "account-delete",
};

export function endpointOf(id: EditorId): EditorEndpoint {
  if (gymIdOf(id) !== null) {
    return "gyms";
  }
  return painIndexOf(id) === null
    ? ENDPOINTS[id as StaticEditorId]
    : "injuries";
}
